<template>
  <div class="tw-w-full">
    <div class="lg:tw-flex tw-pt-4 tw--mx-2">
      <div class="tw-w-full lg:tw-w-2/3 tw-px-2">
        <div v-if="hasAllowance" class="card" data-cy="leave-type-summary">
          <AllowanceSummaryChart :employment-allowance="allowanceSummary" />
        </div>

        <PendingApprovalsSummaryTable
          :loading="pendingApprovalsLoading"
          :pending-approvals="pendingApprovals"
          :cancelling="cancelling"
          :approving="approving"
          @show-info="addPendingApprovalToQuery($event)"
          @decline="declineApprovals"
          @approve="approveApprovals"
        />

        <MyLeaveBreakdownsTable
          :data-leave="myLeave"
          :leave-loading="myLeaveLoading"
          @show-info="addLeaveRequestToQuery"
          @include-declined-changed="myLeaveParams.includeDeclined = $event"
          @include-taken-changed="myLeaveParams.includeTaken = $event"
          @include-booked-changed="myLeaveParams.includeBooked = $event"
        />
      </div>
      <div class="tw-w-full sm:tw-w-2/3 md:tw-w-1/2 lg:tw-w-1/3 tw-px-2">
        <NextSevenDays
          :selected-scope="selectedFilters.scope"
          :display-filters="displayFilters"
          :next-seven-days-leaves="nextSevenDaysLeaves"
          :birthdays="nextSevenDaysBirthdays"
          :holidays="nextSevenDaysHolidays"
          :work-anniversaries="nextSevenDaysWorkAnniversaries"
          @change-filters="changeFilters"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import EventBus from '@/plugins/event-bus'
import ValidatesForm from '@/mixins/ValidatesForm'
import { AllowanceReport, PendingApprovals } from '@/api'
import NextSevenDaysSummaryQuery from '@/graphql/queries/next-seven-days-summary'
import Timezone from '@/support/Timezone'

const AllowanceSummaryChart = () => import('@/components/AllowanceSummaryChart')
const MyLeaveBreakdownsTable = () =>
  import('@/components/my-leave-date/MyLeaveBreakdownsTable')
const NextSevenDays = () => import('@/components/next-seven-days/NextSevenDays')
const PendingApprovalsSummaryTable = () =>
  import('@/components/pending-approvals/PendingApprovalsSummaryTable')

export default {
  name: 'LeaveSummary',

  middleware: 'auth',

  components: {
    MyLeaveBreakdownsTable,
    AllowanceSummaryChart,
    NextSevenDays,
    PendingApprovalsSummaryTable,
  },

  mixins: [ValidatesForm],

  data() {
    return {
      allowanceSummary: null,
      pendingApprovals: [],
      pendingApprovalsLoading: false,
      nextSevenDaysLeaves: [],
      nextSevenDaysHolidays: [],
      nextSevenDaysBirthdays: [],
      nextSevenDaysWorkAnniversaries: [],
      nextSevenDaysEmployments: [],
      nextSevenDaysDepartments: [],
      companySchedule: null,
      myLeave: [],
      myLeaveLoading: false,
      myLeaveParams: {
        includeBooked: true,
        includeTaken: false,
        includeDeclined: false,
      },
      selectedFilters: {
        scope: 'company',
        displayFilters: [],
      },
      cancelling: false,
      approving: false,
    }
  },

  computed: {
    displayFilters() {
      return Object.keys(this.$options.filters)
    },

    canViewPendingApprovals() {
      return this.activeEmployment.is_admin || this.activeEmployment.is_approver
    },

    isDefaultLeaveTypeHasAllowanceType() {
      return this.activeCompany.default_leave_type.allowance_type_id
    },

    hasAllowance() {
      return this.allowanceSummary && this.allowanceSummary.totalAllowance() > 0
    },
  },

  watch: {
    myLeaveParams: {
      deep: true,
      handler: 'fetchMyLeave',
    },

    '$route.query'(query) {
      if (!query.hasOwnProperty('leave-request')) {
        this.fetchLeaves()
      }
    },

    activeCompany: {
      handler(newCompany, oldCompany) {
        if (
          !this.$route.query.hasOwnProperty('leave-request') &&
          newCompany?.id !== oldCompany?.id
        ) {
          this.fetchLeaves()
        }
      },
    },

    activeEmployment: {
      handler(newEmployment, oldEmployment) {
        if (
          oldEmployment?.id === newEmployment?.id &&
          newEmployment.company_id === this.activeCompany.id
        )
          return

        this.fetchLeaves()
      },
    },
  },

  mounted() {
    this.fetchLeaves()
  },

  created() {
    this.$options.filters = {
      Leave: 'applyLeaveFilter',
      'Public Holidays': 'applyHolidaysFilter',
      Birthdays: 'applyBirthdaysFilter',
      'Work Anniversaries': 'applyWorkAnniversariesFilter',
    }

    EventBus.$on('notification-fetched', () => this.fetchLeaves())
  },

  methods: {
    addLeaveRequestToQuery(leave) {
      window.document.body.click()

      this.$router.push(
        {
          query: {
            ...this.$route.query,
            'leave-request': leave.id,
          },
        },
        () => {}
      )
    },

    addPendingApprovalToQuery(request) {
      let path = request.isLeave() ? 'leave-request' : 'overtime'

      window.document.body.click()

      this.$router.push(
        {
          query: {
            ...this.$route.query,
            [path]: request.getKey(),
          },
        },
        () => {}
      )
    },

    fetchLeaves() {
      if (this.activeCompany.id !== this.activeEmployment.company_id) return

      this.fetchPendingApprovals()
      this.fetchAllowanceSummary()
      this.fetchMyLeave()
      this.fetchNextSevenDays()
    },

    async fetchPendingApprovals() {
      if (this.canViewPendingApprovals === false) return

      this.pendingApprovalsLoading = true

      this.pendingApprovals = await PendingApprovals.get(this.activeCompany)

      this.pendingApprovalsLoading = false
    },

    async fetchAllowanceSummary() {
      if (!this.isDefaultLeaveTypeHasAllowanceType) return

      const paginatedAllowances = await AllowanceReport.getDetailsAllowances({
        company_id: this.activeCompany.id,
        date: moment().format('YYYY-MM-DD'),
        employee: this.activeEmployment.id,
        allowance_type: this.activeCompany.default_leave_type.allowance_type_id,
      })

      this.allowanceSummary = paginatedAllowances.items().first()
    },

    async fetchMyLeave() {
      try {
        this.myLeaveLoading = true

        let tableData = []

        if (this.myLeaveParams.includeBooked) {
          tableData = await this.fetchLeavesByType('booked-leaves')
        }

        if (this.myLeaveParams.includeTaken) {
          tableData = [
            ...tableData,
            ...(await this.fetchLeavesByType('taken-leaves')),
          ]
        }

        if (this.myLeaveParams.includeDeclined) {
          tableData = [
            ...tableData,
            ...(await this.fetchLeavesByType('rejected-leaves')),
          ]
        }

        this.myLeave = tableData

        this.myLeaveLoading = false
      } catch (e) {
        this.myLeaveLoading = false
      }
    },

    async fetchLeavesByType(type) {
      try {
        const { data } = await this.$http.get(type, {
          params: {
            company: this.activeCompany.id,
            employment: this.activeEmployment.id,
          },
        })

        return data
      } catch ({ response }) {
        this.validateFromResponse(response, false)
      }
    },

    async fetchNextSevenDays() {
      try {
        await this.$apollo.queries.companySchedule.refetch()
      } catch (error) {
        this.validateGraphQLFromResponse(error, false)
      }
    },

    changeFilters(filters) {
      this.selectedFilters.scope = filters.scope
      this.selectedFilters.displayFilters = filters.displayFilters
      this.applyFilters()
    },

    applyFilters() {
      this.emptyNextSevenDaysData()
      for (let filter of this.selectedFilters.displayFilters) {
        this[this.$options.filters[filter]]()
      }
    },

    applyLeaveFilter() {
      this.nextSevenDaysLeaves = this.mapLeavesToEmployment(
        this.companySchedule?.leave || [],
        this.nextSevenDaysEmployments
      )
    },

    applyHolidaysFilter() {
      this.nextSevenDaysHolidays = this.companySchedule?.holidays || []
    },

    applyBirthdaysFilter() {
      this.nextSevenDaysBirthdays = this.mapBirthdaysToEmployment(
        this.companySchedule?.birthdays || [],
        this.nextSevenDaysEmployments
      )
    },

    applyWorkAnniversariesFilter() {
      this.nextSevenDaysWorkAnniversaries = this.mapWorkAnniversariesToEmployment(
        this.companySchedule?.workAnniversaries || [],
        this.nextSevenDaysEmployments
      )
    },

    emptyNextSevenDaysData() {
      this.nextSevenDaysLeaves = []
      this.nextSevenDaysBirthdays = []
      this.nextSevenDaysHolidays = []
      this.nextSevenDaysWorkAnniversaries = []
    },

    findEmploymentById(employments, id) {
      return employments.find(employment => employment.id === id)
    },

    getEmploymentIdsInCurrentDepartment() {
      const currentDepartment = this.nextSevenDaysDepartments.find(
        department => department.id === this.activeEmployment.department_id
      )
      return currentDepartment?.ownerIds || []
    },

    filterByDepartment(ownerIds) {
      if (this.selectedFilters.scope !== 'department') {
        return ownerIds
      }

      const departmentEmployeeIds = this.getEmploymentIdsInCurrentDepartment()
      return ownerIds.filter(ownerId => departmentEmployeeIds.includes(ownerId))
    },

    formatEmploymentLeaves(leave, employments) {
      const employment = this.findEmploymentById(employments, leave.ownerId)

      return {
        id: leave.id,
        from: this.convertToUtc(leave.from),
        to: this.convertToUtc(leave.to),
        timezone: Timezone.fromName(leave.timezone),
        status: leave.status,
        type: leave.type,
        allowance_unit_is_days: employment?.allowanceUnitIsDays ?? true,
        owner: employment
          ? {
              id: employment.id,
              full_name: employment.fullName,
              avatar_url: employment.avatarUrl,
            }
          : null,
        leave_breakdowns: leave.breakdowns.map(breakdown => ({
          id: breakdown.id,
          from: this.convertToUtc(breakdown.from),
          to: this.convertToUtc(breakdown.to),
          period: breakdown.period,
        })),
      }
    },

    convertToUtc(date) {
      return moment
        .utc(date, 'YYYY-MM-DD HH:mm:ss')
        .format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
    },

    setupNextSevenDaysSummary(companySchedule) {
      this.companySchedule = companySchedule
      this.nextSevenDaysEmployments = companySchedule.employments || []
      this.nextSevenDaysDepartments = companySchedule.departments || []

      this.nextSevenDaysHolidays = companySchedule.holidays || []

      this.nextSevenDaysBirthdays = this.mapBirthdaysToEmployment(
        companySchedule.birthdays || [],
        this.nextSevenDaysEmployments
      )

      this.nextSevenDaysWorkAnniversaries = this.mapWorkAnniversariesToEmployment(
        companySchedule.workAnniversaries || [],
        this.nextSevenDaysEmployments
      )

      this.nextSevenDaysLeaves = this.mapLeavesToEmployment(
        companySchedule.leave || [],
        this.nextSevenDaysEmployments
      )
    },

    mapBirthdaysToEmployment(birthdays, employments) {
      const result = []

      for (const birthday of birthdays) {
        const filteredOwnerIds = this.filterByDepartment(birthday.ownerIds)

        for (const ownerId of filteredOwnerIds) {
          const employment = this.findEmploymentById(employments, ownerId)
          if (employment) {
            const date = moment.utc(birthday.date)
            result.push({
              birthday: `${date.format('DD')}/${date.format('MM')}`,
              owner: {
                id: employment.id,
                full_name: employment.fullName,
                avatar_url: employment.avatarUrl,
              },
            })
          }
        }
      }

      return result
    },

    mapWorkAnniversariesToEmployment(anniversaries, employments) {
      const result = []

      for (const anniversary of anniversaries) {
        const filteredOwnerIds = this.filterByDepartment(anniversary.ownerIds)

        for (const ownerId of filteredOwnerIds) {
          const employment = this.findEmploymentById(employments, ownerId)

          if (employment?.workAnniversaryYearCount) {
            result.push({
              start_date: employment.startDate,
              workAnniversaryYearCount: employment.workAnniversaryYearCount,
              owner: {
                id: employment.id,
                full_name: employment.fullName,
                avatar_url: employment.avatarUrl,
              },
            })
          }
        }
      }

      return result
    },

    mapLeavesToEmployment(leaves, employments) {
      const approvedLeaves = leaves.filter(
        leave => leave.status.toLowerCase() === 'approved'
      )

      if (this.selectedFilters.scope === 'department') {
        const departmentEmployeeIds = this.getEmploymentIdsInCurrentDepartment()
        return approvedLeaves
          .filter(leave => departmentEmployeeIds.includes(leave.ownerId))
          .map(leave => this.formatEmploymentLeaves(leave, employments))
      }

      return approvedLeaves.map(leave =>
        this.formatEmploymentLeaves(leave, employments)
      )
    },

    async declineApprovals() {
      const confirmed = await this.confirm(
        'Are you sure you want to decline all requests?',
        'Confirm Decline'
      )

      if (!confirmed) return

      this.cancelling = true

      try {
        const { data } = await PendingApprovals.decline(this.activeCompany)

        await this.fetchPendingApprovals()

        this.notifyPartiallyDeclined(data?.message)
      } catch ({ response }) {
        this.error(
          response?.data?.message ?? 'We were unable to decline the requests.'
        )
      } finally {
        this.cancelling = false
      }
    },

    notifyPartiallyDeclined(message) {
      if (!message) return

      this.error(message)
    },

    async approveApprovals() {
      const confirmed = await this.confirm(
        'Are you sure you want to approve all requests?',
        'Confirm Approval'
      )

      if (!confirmed) return

      this.approving = true

      await PendingApprovals.approve(this.activeCompany)

      await this.fetchPendingApprovals()
      this.fetchNextSevenDays()

      this.approving = false
    },

    getNextSevenDayParameters() {
      return {
        company: this.activeCompany.id,
        date: moment()
          .startOf('day')
          .utc(true)
          .format('YYYY-MM-DD'),
        interval: 'Days7',
        employmentsQuery: {
          offset: 0,
          limit: 10000,
          employmentIds: [],
          departmentIds: [],
          orderBy: null,
        },
      }
    },
  },

  apollo: {
    companySchedule: {
      debounce: 50,
      query: NextSevenDaysSummaryQuery,
      fetchPolicy: 'no-cache',
      variables() {
        return this.getNextSevenDayParameters()
      },
      result(response) {
        if (!response?.data?.companySchedule) {
          return
        }

        this.setupNextSevenDaysSummary(response.data.companySchedule)
      },
      error() {},
    },
  },
}
</script>
